import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { cmpVersion } from './version';
import { MIN_DEVICE_SERVICE_VERSION, deviceServiceBelowMinimum } from './minimum';
import { configureDevicePort } from './client';

/**
 * App-wide Device Service version status. Any screen that validates the local Device Service reports
 * the connected version via `report(version)`; this compares it against the latest published
 * Device Service download and exposes `updateAvailable`. A global banner nudges the user to update
 * whenever a newer version exists — so the check happens wherever the Device Service is validated.
 */
interface DownloadLite { downloadId: string; name: string; category: string; version: string }

interface DsStatus {
  installed: string | null;
  report: (version: string) => void;
  latest?: string | undefined;
  latestName?: string | undefined;
  updateAvailable: boolean;
  /** Below the floor the server will refuse to capture from. Not the same as "an update exists". */
  belowMinimum: boolean;
  minVersion: string;
}

const Ctx = createContext<DsStatus | null>(null);

export function useDeviceServiceStatus(): DsStatus {
  const c = useContext(Ctx);
  if (!c) throw new Error('useDeviceServiceStatus must be used within DeviceServiceStatusProvider');
  return c;
}

export function DeviceServiceStatusProvider({ children }: { children: ReactNode }) {
  const [installed, setInstalled] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState<string | null>(null);

  /*
   * Apply the user's configured Device Service port to the client as soon as the profile is known.
   *
   * Done here because this provider already wraps the app, so every screen that connects gets the right port
   * without any of them having to know the setting exists. Until it loads, the client uses 8080 — right for
   * nearly everyone, and a wrong guess only costs the one failed connect before this corrects it.
   */
  const profile = useQuery({
    queryKey: ['profile'],
    queryFn: () => api.get<{ deviceServicePort: number | null }>('/profile'),
    staleTime: 300_000,
  });
  const configuredPort = profile.data?.deviceServicePort ?? null;
  useEffect(() => { configureDevicePort(configuredPort); }, [configuredPort]);

  const report = useCallback((version: string) => {
    const v = (version ?? '').trim();
    if (v) setInstalled(v);
  }, []);

  // Only fetch the catalog once we actually know the installed version (i.e. the DS was validated).
  const dl = useQuery({
    queryKey: ['downloads'],
    queryFn: () => api.get<DownloadLite[]>('/downloads'),
    enabled: installed != null,
    staleTime: 300_000,
  });

  const latestItem = useMemo(() => {
    const svc = (dl.data ?? []).filter((d) => d.category === 'device-service');
    return svc.sort((a, b) => cmpVersion(b.version, a.version))[0];
  }, [dl.data]);

  const latest = latestItem?.version;
  const updateAvailable = Boolean(installed && latest && cmpVersion(installed, latest) < 0);
  const belowMinimum = deviceServiceBelowMinimum(installed);

  const value = useMemo<DsStatus>(
    () => ({
      installed, report, latest, latestName: latestItem?.name, updateAvailable,
      belowMinimum, minVersion: MIN_DEVICE_SERVICE_VERSION,
    }),
    [installed, report, latest, latestItem, updateAvailable, belowMinimum],
  );

  // An update nudge is dismissible; being below the floor is not. Capture is refused either way, so a
  // dismissed bar would only hide the reason the next capture fails.
  const showBanner = !belowMinimum && updateAvailable && dismissed !== latest;

  return (
    <Ctx.Provider value={value}>
      {belowMinimum && (
        <div className="ds-update-bar is-blocking" role="alert">
          <span>
            This workstation runs <b>Device Service v{installed}</b>. Capture requires{' '}
            <b>v{MIN_DEVICE_SERVICE_VERSION}</b> or newer — update it to continue capturing.
          </span>
          <span className="ds-update-actions">
            <Link to="/app/downloads" className="btn btn-primary btn-sm">Update now</Link>
          </span>
        </div>
      )}
      {showBanner && (
        <div className="ds-update-bar" role="status">
          <span>
            A new version of the Device Service is available — <b>v{latest}</b>
            {installed ? <> (you have v{installed})</> : null}. Update on each capture workstation.
          </span>
          <span className="ds-update-actions">
            <Link to="/app/downloads" className="btn btn-primary btn-sm">Go to downloads</Link>
            <button type="button" className="ds-update-x" onClick={() => setDismissed(latest ?? null)} aria-label="Dismiss">✕</button>
          </span>
        </div>
      )}
      {children}
    </Ctx.Provider>
  );
}
